import {useMutation} from '@github-ui/react-query'
import {submitAbuseReportMutation as submitAbuseReportQueryId} from '@github-ui/server-persisted-queries'
import type {
  AbuseReportReason,
  SubmitAbuseReportMutationMutation,
} from '@github-ui/server-persisted-queries/types/submitAbuseReportMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'

export type SubmitAbuseReportVariables = {
  owner: string
  repository: string
  contentId: string
  reason: AbuseReportReason
}

type GraphQLResponse = {
  data?: SubmitAbuseReportMutationMutation | null
  errors?: ReadonlyArray<{message?: string}> | null
}

const fallbackError = 'Unable to submit report'

function resultData(result: GraphQLResponse | null): SubmitAbuseReportMutationMutation {
  if (result?.data?.submitAbuseReport) return result.data
  throw new Error(result?.errors?.[0]?.message ?? fallbackError)
}

async function submitAbuseReport({
  owner,
  repository,
  contentId,
  reason,
}: SubmitAbuseReportVariables): Promise<SubmitAbuseReportMutationMutation> {
  const encodedOwner = encodeURIComponent(owner)
  const encodedRepository = encodeURIComponent(repository)
  const query = new URLSearchParams({queryId: submitAbuseReportQueryId})
  const path = `/${encodedOwner}/${encodedRepository}/comments/abuse_report?${query}`
  const response = await reactFetchJSON(path, {method: 'POST', body: {comment_id: contentId, reason}})

  if (!response.ok) {
    throw new Error(`${fallbackError} (${response.status})`)
  }

  return resultData((await response.json()) as GraphQLResponse)
}

export function useSubmitAbuseReportMutation() {
  return useMutation<SubmitAbuseReportMutationMutation, Error, SubmitAbuseReportVariables>({
    mutationFn: submitAbuseReport,
  })
}
